/**
 * Compact horizontal product card for inline chat-pane rendering.
 *
 * Production parity: mirrors the prior engine's `LaunchSingleProduct` component.
 * Renders when `productDetails` arrives — the full ProductDetailsPanel goes
 * to the left panel while this compact summary appears inline in chat messages.
 *
 * Layout: [image 64×64] [name · rating · price] [View link]
 *
 * Card-body clicks off the product URL invoke `onProductClick` (host navigation);
 * on the product URL they fall through to `onProductSelect` (inline/panel drilldown).
 */

import type { UIElement } from '../../common/types.js';
import type { ChatUISpecRenderContext } from '../types.js';
import { formatPrice } from '../../common/price-formatter.js';
import { isSafeUrl, safeSetAttribute } from '../../common/safe-html.js';
import { addImageErrorHandler, createStarRatingElement, isSameProductPath } from '../../common/product-utils.js';
import {
  campaignReasonForDisplay,
  createCampaignPriceBadge,
  createCampaignReasonElement,
  resolveCampaignBadgeLogoUrl,
  resolveOriginalPriceStyle,
} from './product-price-layout.js';

export function renderProductSummaryCard(element: UIElement, ctx: ChatUISpecRenderContext): HTMLElement {
  const product = (element.props?.['product'] ?? element.props) as Record<string, unknown> | undefined;

  const card = document.createElement('div');
  card.className = 'gengage-chat-product-summary gds-card';
  card.dataset['gengagePart'] = 'product-summary-card';
  if (!product) return card;

  card.classList.add('gds-clickable');

  // --- Image (left side) ---
  const imageUrl = product['imageUrl'] as string | undefined;
  if (imageUrl && isSafeUrl(imageUrl)) {
    const imgWrap = document.createElement('div');
    imgWrap.className = 'gengage-chat-product-summary__image';
    imgWrap.dataset['gengagePart'] = 'product-summary-image';
    const img = document.createElement('img');
    img.loading = 'lazy';
    safeSetAttribute(img, 'src', imageUrl);
    const name = product['name'] as string | undefined;
    img.alt = name || 'Product image';
    addImageErrorHandler(img);
    imgWrap.appendChild(img);
    card.appendChild(imgWrap);
  }

  // --- Content (right side) ---
  const content = document.createElement('div');
  content.className = 'gengage-chat-product-summary__content';
  content.dataset['gengagePart'] = 'product-summary-content';

  // Product name (brand + name)
  const brand = product['brand'] as string | undefined;
  const name = product['name'] as string | undefined;
  if (name) {
    const nameEl = document.createElement('div');
    nameEl.className = 'gengage-chat-product-summary__name';
    nameEl.dataset['gengagePart'] = 'product-summary-name';
    // Only prepend brand if name doesn't already start with it
    const needsBrand = brand && !name.toLowerCase().startsWith(brand.toLowerCase());
    const fullName = needsBrand ? `${brand} ${name}` : name;
    nameEl.textContent = fullName;
    nameEl.title = fullName;
    content.appendChild(nameEl);
  }

  // Rating
  const rating = product['rating'];
  const reviewCount = product['reviewCount'];
  if (ctx.hideUserReviews !== true && typeof rating === 'number' && Number.isFinite(rating) && rating > 0) {
    const ratingRow = document.createElement('div');
    ratingRow.className = 'gengage-chat-product-summary__rating';
    ratingRow.dataset['gengagePart'] = 'product-summary-rating';
    ratingRow.appendChild(createStarRatingElement(rating));
    if (typeof reviewCount === 'number' && Number.isFinite(reviewCount)) {
      const count = document.createElement('span');
      count.className = 'gengage-chat-product-summary__review-count';
      count.textContent = ` (${reviewCount})`;
      ratingRow.appendChild(count);
    }
    content.appendChild(ratingRow);
  }

  // Price row (+ optional campaign line)
  const price = product['price'] as string | undefined;
  const originalPrice = product['originalPrice'] as string | undefined;
  if (price) {
    const campaignReason = campaignReasonForDisplay(ctx, product);
    const listStyle = resolveOriginalPriceStyle(ctx, product);
    const hasDiscount = !!(originalPrice && originalPrice !== price);

    const priceRow = document.createElement('div');
    priceRow.className = 'gengage-chat-product-summary__price';
    priceRow.dataset['gengagePart'] = 'product-summary-price';

    const frameDiscounted = !!(campaignReason && hasDiscount);
    const logoUrl = resolveCampaignBadgeLogoUrl(ctx, product);

    if (frameDiscounted) {
      const badge = createCampaignPriceBadge({
        reasonText: campaignReason!,
        salePriceFormatted: formatPrice(price, ctx.pricing),
        ...(logoUrl !== undefined ? { logoUrl } : {}),
      });
      if (hasDiscount && listStyle === 'inline') {
        priceRow.classList.add('gengage-chat-product-summary__price--inline');
        priceRow.appendChild(badge);
        const sep = document.createElement('span');
        sep.className = 'gengage-chat-product-summary__price-sep';
        sep.setAttribute('aria-hidden', 'true');
        const orig = document.createElement('span');
        orig.className = 'gengage-chat-product-summary__price-original';
        orig.textContent = formatPrice(originalPrice!, ctx.pricing);
        priceRow.appendChild(sep);
        priceRow.appendChild(orig);
      } else {
        const orig = document.createElement('span');
        orig.className = 'gengage-chat-product-summary__price-original';
        orig.textContent = formatPrice(originalPrice!, ctx.pricing);
        priceRow.appendChild(orig);
        priceRow.appendChild(document.createTextNode(' '));
        priceRow.appendChild(badge);
      }
      content.appendChild(priceRow);
    } else {
      if (hasDiscount && listStyle === 'inline') {
        priceRow.classList.add('gengage-chat-product-summary__price--inline');
        const cur = document.createElement('span');
        cur.className = 'gengage-chat-product-summary__price-current';
        cur.textContent = formatPrice(price, ctx.pricing);
        const sep = document.createElement('span');
        sep.className = 'gengage-chat-product-summary__price-sep';
        sep.setAttribute('aria-hidden', 'true');
        const orig = document.createElement('span');
        orig.className = 'gengage-chat-product-summary__price-original';
        orig.textContent = formatPrice(originalPrice!, ctx.pricing);
        priceRow.appendChild(cur);
        priceRow.appendChild(sep);
        priceRow.appendChild(orig);
      } else if (hasDiscount) {
        const orig = document.createElement('span');
        orig.className = 'gengage-chat-product-summary__price-original';
        orig.textContent = formatPrice(originalPrice!, ctx.pricing);
        priceRow.appendChild(orig);
        priceRow.appendChild(document.createTextNode(' '));
        const current = document.createElement('span');
        current.className = 'gengage-chat-product-summary__price-current';
        current.textContent = formatPrice(price, ctx.pricing);
        priceRow.appendChild(current);
      } else {
        const current = document.createElement('span');
        current.className = 'gengage-chat-product-summary__price-current';
        current.textContent = formatPrice(price, ctx.pricing);
        priceRow.appendChild(current);
      }
      if (campaignReason) {
        const stack = document.createElement('div');
        stack.className = 'gengage-chat-product-summary__price-stack';
        stack.appendChild(createCampaignReasonElement(campaignReason));
        stack.appendChild(priceRow);
        content.appendChild(stack);
      } else {
        content.appendChild(priceRow);
      }
    }
  }

  card.appendChild(content);

  // --- CTA (right edge): add-to-cart when purchasable, else safe product URL link ---
  const url = product['url'] as string | undefined;
  const sku = product['sku'] as string | undefined;
  const cartCodeRaw = product['cartCode'] ?? product['cart_code'];
  const cartCode = typeof cartCodeRaw === 'string' && cartCodeRaw.trim() ? cartCodeRaw.trim() : undefined;
  const inStock = product['inStock'];
  const hasCart = !!(sku && cartCode && inStock !== false);

  if (hasCart) {
    const cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'gengage-chat-product-summary__cta gds-chip gds-chip-active';
    cta.dataset['gengagePart'] = 'product-summary-cta';
    cta.textContent = ctx.i18n?.addToCartButton ?? 'Add to Cart';
    cta.addEventListener('click', (e) => {
      e.stopPropagation();
      ctx.onAction({
        title: ctx.i18n?.addToCartButton ?? 'Add to Cart',
        type: 'addToCart',
        payload: { sku: sku!, cartCode: cartCode!, quantity: 1 },
      });
    });
    card.appendChild(cta);
  } else if (url && isSafeUrl(url)) {
    const cta = document.createElement('a');
    cta.className = 'gengage-chat-product-summary__cta gds-chip gds-chip-active';
    cta.dataset['gengagePart'] = 'product-summary-cta';
    safeSetAttribute(cta, 'href', url);
    safeSetAttribute(cta, 'target', '_blank');
    safeSetAttribute(cta, 'rel', 'noopener noreferrer');
    cta.textContent = ctx.i18n?.productCtaLabel ?? 'View';
    card.appendChild(cta);
  }

  card.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('a, .gengage-chat-product-summary__cta')) return;

    const safeProductUrl = url && isSafeUrl(url) ? url : undefined;
    const productSku = sku == null || sku === '' ? '' : String(sku);
    const productName = typeof name === 'string' ? name : undefined;

    if (safeProductUrl && ctx.onProductClick && !isSameProductPath(safeProductUrl)) {
      ctx.onProductClick({
        sku: productSku,
        url: safeProductUrl,
        ...(productName ? { name: productName } : {}),
      });
      return;
    }

    ctx.onProductSelect?.(product);
  });

  return card;
}
